import * as React from "react";

import { extractTopicReferences } from "@/shared/domain/skills/topicReference";
import { useAllTopicKeys } from "@/shared/domain/topics/hooks";

export interface RelevantTopics {
  /** Every topic name in the org, or [] until the catalog resolves. */
  topicNames: string[];
  /** True while the catalog is loading — hold Save until it settles. */
  isCatalogLoading: boolean;
  /** `relevant_topics` for a body, or undefined when it can't be derived. */
  relevantTopicsFor: (body: string) => string[] | undefined;
}

/**
 * Resolve a body's `[[topic]]` references against the org's topic catalog.
 *
 * Returns undefined — not [] — when the catalog isn't available, because the
 * request layer reads [] as "clear the stored list" and undefined as "leave it
 * unchanged". Without that distinction a save made while the catalog query was
 * erroring would wipe references the author never touched.
 */
export function useRelevantTopics(
  orgId: string | undefined,
  opts?: { enabled?: boolean },
): RelevantTopics {
  const topicKeysQuery = useAllTopicKeys(orgId, opts);
  const topicNames = React.useMemo(
    () => topicKeysQuery.data?.topic_names ?? [],
    [topicKeysQuery.data],
  );
  const isSuccess = topicKeysQuery.isSuccess;

  const relevantTopicsFor = React.useCallback(
    (body: string): string[] | undefined =>
      isSuccess ? extractTopicReferences(body, topicNames) : undefined,
    [isSuccess, topicNames],
  );

  return {
    topicNames,
    isCatalogLoading: topicKeysQuery.isLoading,
    relevantTopicsFor,
  };
}
